// The install step's view, from the stages the daemon reports while it installs.

export type InstallStage = "detect" | "check_node" | "install" | "verify";
export type InstallStageReport = "active" | "done" | "failed";
export type InstallStepState = "pending" | InstallStageReport;

export const INSTALL_STAGES: readonly InstallStage[] = [
  "detect",
  "check_node",
  "install",
  "verify",
];

export function isInstallStage(value: unknown): value is InstallStage {
  return INSTALL_STAGES.includes(value as InstallStage);
}

export function isInstallStageReport(value: unknown): value is InstallStageReport {
  return value === "active" || value === "done" || value === "failed";
}

const CLI_NAMES: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex CLI",
  copilot: "GitHub Copilot CLI",
};

export function cliName(providerId: string, label: string): string {
  return CLI_NAMES[providerId] ?? label;
}

export interface InstallStepView {
  stage: InstallStage;
  label: string;
  state: InstallStepState;
}

export interface InstallProgressView {
  /** No stages reported (an older daemon): one indeterminate line, never invented steps. */
  staged: boolean;
  failed: boolean;
  steps: InstallStepView[];
  current: InstallStepView | undefined;
}

export function installProgressView(input: {
  providerId: string;
  label: string;
  machineLabel: string;
  status: "installing" | "install-failed";
  reports: Partial<Record<InstallStage, InstallStageReport>>;
}): InstallProgressView {
  const failed = input.status === "install-failed";
  const labels: Record<InstallStage, string> = {
    detect: `Checking ${input.machineLabel}`,
    check_node: "Setting up Node.js",
    install: `Installing ${cliName(input.providerId, input.label)}`,
    verify: "Making sure it starts",
  };
  const steps: InstallStepView[] = INSTALL_STAGES.map((stage) => ({
    stage,
    label: labels[stage],
    state: input.reports[stage] ?? "pending",
  }));
  // A failure no stage reported (a refused request): the running stage is the one that stopped.
  if (failed && !steps.some((s) => s.state === "failed")) {
    const running = steps.find((s) => s.state === "active");
    if (running) running.state = "failed";
  }
  const staged = steps.some((s) => s.state !== "pending");
  const current =
    steps.find((s) => s.state === "failed") ?? steps.find((s) => s.state === "active");
  return { staged, failed, steps, current };
}

/** "1:05" — how long the install has been running. */
export function elapsedLabel(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
